import { useMemo } from 'react'

import type { TeamMemberDto } from '@api'
import { clsx } from 'clsx'
import { useUnit } from 'effector-react'
import { Controller } from 'react-hook-form'

import IconChevronDown from '@/assets/icons/icon-chevron-down.svg'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.tsx'
import { $account } from '@/models/account.ts'
import { useFetchTeamTable } from '@/modules/team/hooks/use-fetch-team-table'

function useAssigneeOptions() {
  const team = useFetchTeamTable()
  const account = useUnit($account)

  return useMemo(
    () => {
      return (
        team.data
          ?.sort((left, right) => compareMember(left, right, account))
          .map((member) => ({
            id: member.id,
            name: member.id === account?.memberId ? 'Me' : member.name,
          })) ?? []
      )
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [team.data, account?.memberId],
  )
}

export const AssigneeSelector = () => {
  const assigneeOptions = useAssigneeOptions()

  return (
    <Controller
      name="assignees"
      render={({ field, fieldState: { error } }) => {
        const selectedNames = assigneeOptions
          .filter((option) => field.value?.includes(option.id))
          .map((option) => option.name)

        return (
          <div className="flex flex-col gap-2">
            <label
              htmlFor="assignees"
              className="text-title text-sm font-medium"
            >
              Assignee
            </label>
            <DropdownMenu>
              <DropdownMenuTrigger className="bg-card text-title focus:border-primary-500 focus:outline-primary-500 flex max-h-[40px] w-[240px] items-center justify-between rounded-md border  px-3 py-2">
                <span className="truncate">
                  {selectedNames.length === 0
                    ? 'Select assignee'
                    : selectedNames.join(', ')}
                </span>
                <IconChevronDown className="text-title ml-auto h-4 w-4 flex-shrink-0" />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="z-[100] max-h-[400px] w-64 overflow-y-auto p-2"
                align="start"
                forceMount
              >
                <DropdownMenuGroup className="flex flex-col gap-[2px]">
                  {assigneeOptions.map((option) => {
                    const included = field.value?.includes(option.id)

                    return (
                      <DropdownMenuItem
                        key={option.id}
                        className={clsx(
                          'group cursor-pointer gap-2 rounded-md',
                          {
                            'bg-primary-500 text-white': included,
                            'text-title hover:bg-primary-500/20 hover:text-primary-500':
                              !included,
                          },
                        )}
                        onClick={() => {
                          if (!included) {
                            field.onChange([option.id])

                            return
                          }
                          field.onChange(
                            field.value?.filter(
                              (id: number) => id !== option.id,
                            ),
                          )
                        }}
                      >
                        {option.name}
                      </DropdownMenuItem>
                    )
                  })}
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
            {error && (
              <p className="text-danger-default mt-2 text-xs">
                {error.message}
              </p>
            )}
          </div>
        )
      }}
    />
  )
}

function compareMember(
  left: TeamMemberDto,
  right: TeamMemberDto,
  account?: { memberId?: number } | null,
) {
  if (left.id === right.id) {
    return 0
  }
  if (left.id === account?.memberId) {
    return -1
  }
  if (right.id === account?.memberId) {
    return 1
  }

  return left.name.localeCompare(right.name)
}
